import type { LinksFunction, LoaderFunction, DataFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  redirect,
  useLocation,
  useMatches,
  isRouteErrorResponse,
  useRouteError,
} from '@remix-run/react';
import { Toast } from 'radix-ui';
import { useEffect, useState } from 'react';

import { CookieConsent } from '~/components/CookieConsent';
import { ErrorPage } from '~/components/ErrorPage';
import { GoogleTagManager, GoogleTagManagerNoscript } from '~/components/GoogleTagManager';
import { OrganizationJsonLd } from '~/components/JsonLd';
import { NotFoundPage } from '~/components/NotFoundPage';
import { HomePage } from '~/components/home/HomePage';
import { AuthenticatedLayout } from '~/components/layouts/AuthenticatedLayout';
import { PublicLayout } from '~/components/layouts/PublicLayout';
import { ZenLayout } from '~/components/layouts/ZenLayout';
import { SupportProvider } from '~/components/support/SupportContext';
import { ToastProvider } from '~/components/ui/ToastContext';
import { POST_AUTH_LANDING_PATH } from '~/config/landing';
import { isProfileCompletenessBannerEnabled } from '~/config/profile-completeness';
import { isPublicDemoEnabled } from '~/config/public-demo';
import { getUserAttributes } from '~/services/auth.server';
import { isBaaGateAllowlisted, requiresBaa } from '~/services/baa.server';
import { shouldShowLeadsNav } from '~/services/leads-nav.server';
import { loginEventIdToTouch, touchLoginEvent } from '~/services/login-activity.server';
import { countPendingSignatureRequests } from '~/services/note-signing-routing.server';
import { getUnreadNotificationCount } from '~/services/notifications.server';
import { getSession, getSessionAttribute, logout } from '~/services/session.server';
import { getCompanyMainOfficeId } from '~/utils/embed-permissions.server';
import { formatDoctorNameWithDegree } from '~/utils/formatters';
import {
  isOnboardingGateAllowlisted,
  shouldEnforceOnboardingGate,
} from '~/utils/onboarding-gate.server';
import {
  computeProfileCompleteness,
  getProfileCompletionTarget,
  type ProfileCompletionTarget,
} from '~/utils/profile-completeness';
import { formatRoleForDisplay } from '~/utils/role-helpers';

import './tailwind.css';
import './styles/home.css';
import { db } from './services/db.server';

export const links: LinksFunction = () => [
  {
    rel: 'preload',
    href: '/fonts/inter-latin-normal.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'preload',
    href: '/images/home/toothreferral-logo-sm.png',
    as: 'image',
  },
  {
    rel: 'stylesheet',
    href: 'https://cdn.jsdelivr.net/gh/orestbida/cookieconsent@3.1.0/dist/cookieconsent.css',
  },
];

export const loader: LoaderFunction = async ({ request }: DataFunctionArgs) => {
  const url = new URL(request.url);

  // Allow the embed route to be accessed without authentication.
  // Still surface analytics tracking IDs so embed iframes can load GA4 / Pixel.
  if (url.pathname.startsWith('/embed')) {
    return json({
      isAuthenticated: false,
      ENV: {
        NODE_ENV: process.env.NODE_ENV,
        SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT,
      },
    });
  }

  if (url.pathname.startsWith('/demo-builder') || url.pathname.startsWith('/contact-us-builder')) {
    return json({
      isAuthenticated: false,
      publicDemoEnabled: isPublicDemoEnabled(),
      ENV: {
        NODE_ENV: process.env.NODE_ENV,
        SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT,
      },
    });
  }

  const session = await getSession(request);
  const isAuthenticated = session.has('accessToken');
  const isDemoSession = session.get('isDemoSession') === true;
  const isReadOnlyDemoSession = session.get('readOnly') === true;
  const demoTourRequested = session.get('demoTourRequested') === true;
  let userInfo = null;
  let logoPath = null;
  let companyName = null;
  let profile = null;
  let profileCompleteness: number | null = null;
  let profileCompletionTarget: ProfileCompletionTarget | null = null;
  let userRole = 'GENERAL_DENTIST';
  let unreadNotificationCount = 0;
  let newLeadsCount = 0;
  let pendingSignatureCount = 0;
  let showLeadsNav = false;
  let mainOfficeId = '';
  let mainOfficeName = '';
  if (isAuthenticated) {
    const accessToken = session.get('accessToken');
    if (!accessToken) {
      // If access token is undefined but isAuthenticated is true,
      // clear the session and redirect to home
      return logout(request);
    }

    try {
      const companyId = await getSessionAttribute(request, 'companyId');
      const userId = await getSessionAttribute(request, 'userId');

      // Roll the staff-activity presence marker (#1182). Fire-and-forget:
      // touchLoginEvent throttles itself and swallows failures.
      const loginEventId = loginEventIdToTouch(session);
      if (loginEventId) void touchLoginEvent(loginEventId);

      if (userId) {
        unreadNotificationCount = await getUnreadNotificationCount(userId);
        pendingSignatureCount = await countPendingSignatureRequests(userId);
      }
      if (companyId) {
        newLeadsCount = await db.lead.count({ where: { companyId, status: 'NEW' } });
        showLeadsNav = await shouldShowLeadsNav(companyId, newLeadsCount);
      }

      // getUserAttributes now handles both Cognito and social login with fallback
      userInfo = await getUserAttributes(accessToken, userId);

      // Get user profile and role in a single query (include offices for switcher)
      if (userId) {
        const doctorProfile = await db.doctorProfile.findUnique({
          where: { id: userId },
          select: {
            firstName: true,
            lastName: true,
            role: true,
            profilePicture: true,
            companyId: true,
            officeId: true,
            email: true,
            phone: true,
            type: true,
            degree: true,
            isOnboarded: true,
            mustSetPassword: true,
            insuranceCarriers: {
              select: {
                insuranceCarrierId: true,
              },
            },
            doctorSkills: {
              select: { id: true },
            },
            office: {
              select: {
                id: true,
                streetAddress: true,
                city: true,
                state: true,
                zip: true,
                phone: true,
                email: true,
              },
            },
            offices: {
              include: { office: { select: { id: true, name: true } } },
            },
            company: {
              select: { companyLookupPinHash: true },
            },
          },
        });

        if (doctorProfile) {
          profile = doctorProfile;
          // Format the role for display
          userRole = formatRoleForDisplay(doctorProfile.role);
        } else {
          // Fallback: try to get profile by email if userId query fails
          profile = await db.doctorProfile.findUnique({
            where: { email: userInfo.email },
            select: {
              firstName: true,
              lastName: true,
              role: true,
              profilePicture: true,
              companyId: true,
              officeId: true,
              email: true,
              phone: true,
              type: true,
              degree: true,
              isOnboarded: true,
              mustSetPassword: true,
              insuranceCarriers: {
                select: {
                  insuranceCarrierId: true,
                },
              },
              doctorSkills: {
                select: { id: true },
              },
              office: {
                select: {
                  id: true,
                  streetAddress: true,
                  city: true,
                  state: true,
                  zip: true,
                  phone: true,
                  email: true,
                },
              },
              offices: {
                include: { office: { select: { id: true, name: true } } },
              },
              company: {
                select: { companyLookupPinHash: true },
              },
            },
          });

          if (profile?.role) {
            userRole = formatRoleForDisplay(profile.role);
          }
        }
        if (profile && isProfileCompletenessBannerEnabled()) {
          const completionInput = {
            role: profile.role,
            firstName: profile.firstName,
            lastName: profile.lastName,
            phone: profile.phone,
            profilePicture: profile.profilePicture,
            degree: profile.degree,
            insuranceCount: profile.insuranceCarriers.length,
            skillsCount: profile.doctorSkills.length,
            office: profile.office,
            companyLookupPinHash: profile.company?.companyLookupPinHash ?? null,
          };
          profileCompleteness = computeProfileCompleteness(completionInput);
          profileCompletionTarget = getProfileCompletionTarget(completionInput);
        }
      }

      if (companyId) {
        const company = await db.company.findUnique({
          where: { id: companyId },
          select: { logoPath: true, name: true },
        });
        logoPath = company?.logoPath;
        companyName = company?.name;

        const resolvedMainOfficeId = await getCompanyMainOfficeId(companyId);
        if (resolvedMainOfficeId) {
          mainOfficeId = resolvedMainOfficeId;
          const mainOffice = await db.office.findUnique({
            where: { id: resolvedMainOfficeId },
            select: { name: true },
          });
          mainOfficeName = mainOffice?.name ?? '';
        }
      }

      // Onboarding gate: authenticated users who have not finished onboarding must stay
      // in the onboarding flow (API/auth callbacks remain reachable for form submissions).
      if (
        shouldEnforceOnboardingGate({
          userId,
          isDemoSession,
          isReadOnlyDemoSession,
          isOnboarded: profile?.isOnboarded,
          pathname: url.pathname,
        })
      ) {
        return redirect('/onboarding');
      }

      // Password gate (#1226): a token-activated team member must choose their own
      // password before using the app — their provisional password is known to no one,
      // so skipping this screen would strand them at next sign-in. Reuses the
      // onboarding allowlist (/onboarding/*, /logout, /api/*, /auth/*).
      if (
        !isDemoSession &&
        !isReadOnlyDemoSession &&
        profile &&
        profile.mustSetPassword &&
        !isOnboardingGateAllowlisted(url.pathname)
      ) {
        return redirect('/onboarding/set-password');
      }

      // BAA gate: onboarded users without a valid acceptance are forced to /baa.
      // Runs on every authenticated page request (resource routes bypass root).
      // Exempt read-only public demo sessions: a demo visitor must not be pushed into
      // the BAA acceptance flow (and could not complete it under read-only anyway).
      if (
        userId &&
        !isDemoSession &&
        profile &&
        'isOnboarded' in profile &&
        profile.isOnboarded &&
        !isBaaGateAllowlisted(url.pathname) &&
        (await requiresBaa(userId))
      ) {
        return redirect('/baa');
      }
      // If user is authenticated and trying to access the root route, redirect to the landing page
      if (new URL(request.url).pathname === '/') {
        return redirect(POST_AUTH_LANDING_PATH);
      }
    } catch (error) {
      // If there's an error with the access token (e.g. expired or invalid)
      // clear the session and redirect to home
      return logout(request);
    }
  }

  // Check if user is super admin
  const isSuperAdmin = userInfo
    ? (await import('~/utils/admin-auth.server')).isSuperAdmin(userInfo.email)
    : false;

  const userOffices =
    profile && 'offices' in profile && Array.isArray(profile.offices)
      ? profile.offices.map((o: { office: { id: string; name: string } }) => ({
          id: o.office.id,
          name: o.office.name,
        }))
      : [];
  const currentOfficeId = profile && 'officeId' in profile ? (profile.officeId ?? '') : '';
  const currentOfficeName = userOffices.find(o => o.id === currentOfficeId)?.name ?? '';

  return json({
    isAuthenticated,
    isDemoSession,
    isReadOnlyDemoSession,
    demoTourRequested,
    unreadNotificationCount,
    newLeadsCount,
    pendingSignatureCount,
    showLeadsNav,
    ...(userInfo && {
      firstName: profile?.firstName ?? userInfo.firstName,
      lastName: profile?.lastName ?? userInfo.lastName,
      fullName: formatDoctorNameWithDegree(
        profile?.firstName ?? userInfo.firstName,
        profile?.lastName ?? userInfo.lastName,
        profile?.role,
        profile?.degree ?? null
      ),
      email: userInfo.email,
      companyLogo: logoPath,
      companyName: companyName,
      userRole,
      userRoleRaw: profile?.role ?? null,
      profilePicture: profile?.profilePicture,
      isSuperAdmin,
      canInviteNetwork: !!(
        profile &&
        'companyId' in profile &&
        'officeId' in profile &&
        profile.companyId &&
        profile.officeId
      ),
      userOffices,
      currentOfficeId,
      currentOfficeName,
      mainOfficeId,
      mainOfficeName,
      profileCompleteness,
      profileCompletionTarget,
    }),
    publicDemoEnabled: isPublicDemoEnabled(),
    ENV: {
      VAPID_PUBLIC_KEY: process.env.VAPID_PUBLIC_KEY,
      NODE_ENV: process.env.NODE_ENV,
      ENABLE_SW: process.env.ENABLE_SW,
      CDN_BASE_URL: process.env.CDN_BASE_URL,
      STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
      SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT,
    },
  });
};

const SEO_INDEXED_PATHS = new Set([
  '/',
  '/find-a-dentist',
  '/find-a-plan',
  '/contact',
  '/privacy-policy',
  '/terms',
  '/advisors',
  '/platform',
  '/search/membership-plans',
  '/demo-builder/tour',
  '/contact-us-builder',
  '/faq',
  '/blog',
  '/endodontist-referrals',
  '/periodontist-referrals',
  '/oral-surgeon-referrals',
]);

const SEO_INDEXED_PREFIXES = ['/blog/'];

export function Layout({ children }: { children: React.ReactNode }) {
  const [isClient, setIsClient] = useState(false);
  const location = useLocation();

  // Check if we're in an embed context
  const isEmbedRoute = location.pathname.startsWith('/embed');
  const shouldNoIndex =
    !SEO_INDEXED_PATHS.has(location.pathname) &&
    !SEO_INDEXED_PREFIXES.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="google-site-verification"
          content="6TQEOpuITFQqU5A6OvRc60AYynznocrIuBz_YEUwTCo"
        />
        {shouldNoIndex && <meta name="robots" content="noindex, nofollow" />}
        <GoogleTagManager />
        <Meta />
        <Links />
        <OrganizationJsonLd />
      </head>
      <body>
        <GoogleTagManagerNoscript />
        <Toast.Provider swipeDirection="right">
          <ToastProvider>{children}</ToastProvider>
          <ScrollRestoration />
          <Scripts />
          <Toast.Viewport className="fixed top-0 right-0 z-[2147483647] m-0 flex w-[390px] max-w-[100vw] list-none flex-col gap-2.5 p-6 outline-none" />
          {/* Only render cookie consent on client for non-embed routes */}
          {isClient && !isEmbedRoute && <CookieConsent />}
        </Toast.Provider>
        {/* Strip stray "$" text nodes that Remix's streaming SSR occasionally leaks
            as direct children of <body>. These are framework-level chunk markers and
            must never render as visible text. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var strip=function(root){if(!root)return;root.childNodes.forEach(function(n){if(n.nodeType===3&&n.nodeValue&&n.nodeValue.trim()==='$')n.remove();});};strip(document.body);new MutationObserver(function(){strip(document.body);}).observe(document.body,{childList:true});}catch(e){}})();`,
          }}
        />
      </body>
    </html>
  );
}

interface RouteHandle {
  bareLayout?: boolean;
}

/**
 * Session-expired UI + redirect. Inline <script> redirects are unreliable here because
 * React does not execute scripts inserted via dangerouslySetInnerHTML on client renders
 * (common after SPA navigations when a loader throws 401), while SSR may run them — hence
 * intermittent behavior. useEffect always runs after mount; meta refresh covers no-JS.
 */
function SessionExpiredRedirect({ loginUrl }: { loginUrl: string }) {
  useEffect(() => {
    window.location.replace(loginUrl);
  }, [loginUrl]);

  return (
    <div className="text-center">
      <div className="mb-8">
        <div className="text-8xl font-bold text-gray-200 mb-4">401</div>
        <h1 className="text-3xl font-semibold text-gray-900 mb-4">Session expired</h1>
        <p className="text-lg text-gray-600 mb-8">
          You&apos;ve been logged out due to inactivity. Redirecting you to sign in…
        </p>
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const location = useLocation();

  const is401 = isRouteErrorResponse(error) && error.status === 401;
  const isEmbedRoute = location.pathname.startsWith('/embed');
  const loginUrl =
    is401 && !isEmbedRoute
      ? (() => {
          const currentPath = `${location.pathname}${location.search}${location.hash}`;
          const params = new URLSearchParams();
          params.set('redirectTo', currentPath);
          params.set('session', 'expired');
          return `/login?${params.toString()}`;
        })()
      : null;

  let errorContent;

  if (isEmbedRoute) {
    const status = isRouteErrorResponse(error) ? error.status : 500;
    const message = isRouteErrorResponse(error)
      ? String(error.data || error.statusText || 'This embedded form is unavailable.')
      : error instanceof Error
        ? error.message
        : 'An unexpected error occurred.';
    errorContent = (
      <div className="p-6 font-sans text-red-800">
        <h1 className="text-xl font-semibold">Embedding Error</h1>
        <p className="mt-2 text-sm">{message}</p>
        <p className="mt-1 text-xs text-gray-500">Status: {status}</p>
      </div>
    );
  } else if (is401 && loginUrl) {
    errorContent = <SessionExpiredRedirect loginUrl={loginUrl} />;
  } else if (isRouteErrorResponse(error) && error.status === 404) {
    errorContent = <NotFoundPage />;
  } else {
    // Handle other errors
    const status = isRouteErrorResponse(error) ? error.status : 500;
    const title = isRouteErrorResponse(error) ? error.statusText : 'Something went wrong';
    const message =
      error instanceof Error
        ? error.message
        : 'An unexpected error occurred. Please try again later.';

    errorContent = <ErrorPage status={status} title={title} message={message} />;
  }

  return (
    <html>
      <head>
        <title>
          {isRouteErrorResponse(error)
            ? `${error.status} ${error.statusText}`
            : error instanceof Error
              ? error.message
              : 'Unknown Error'}
        </title>
        {is401 && loginUrl ? <meta httpEquiv="refresh" content={`0;url=${loginUrl}`} /> : null}
        <Meta />
        <Links />
      </head>
      <body>
        <ZenLayout>{errorContent}</ZenLayout>
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const { isAuthenticated, ENV, email, fullName } = useLoaderData<typeof loader>();
  const location = useLocation();
  const matches = useMatches();
  const bareLayout = matches.some(m => (m.handle as RouteHandle)?.bareLayout);

  const wrapWithSupport = (content: React.ReactNode) =>
    isAuthenticated && email && fullName ? (
      <SupportProvider userEmail={email} userName={fullName}>
        {content}
      </SupportProvider>
    ) : (
      content
    );

  // Inject environment variables once for all routes
  const envScript = (
    <script
      dangerouslySetInnerHTML={{
        __html: `window.ENV = ${JSON.stringify(ENV)}`,
      }}
    />
  );

  // List of public routes that don't require authentication
  const publicRoutes = [
    '/login',
    '/signup',
    '/verify',
    '/demo',
    '/demo/start',
    '/forgot-password',
    '/reset-password',
    '/set-password',
    '/doctors',
    '/sign-complete',
    '/advisors',
    '/opt-out', // Added opt-out as a public route
    '/unsubscribe',
    '/digests/unsubscribe',
    '/search/membership-plans',
    '/find-a-dentist',
    '/find-a-plan',
    '/privacy-policy',
    '/contact',
    '/demo-builder',
    '/demo-builder/tour',
    '/contact-us-builder',
  ];
  const isDemoPublicCompanySiteRoute = /^\/public\/companies\/[^/]+\/site\/?$/.test(
    location.pathname
  );
  const isPublicRoute =
    publicRoutes.includes(location.pathname) ||
    location.pathname.startsWith('/search/membership-plans') ||
    location.pathname.startsWith('/demo-builder') ||
    location.pathname.startsWith('/contact-us-builder') ||
    isDemoPublicCompanySiteRoute;
  const isEmbedRoute = location.pathname.startsWith('/embed');

  // Routes that use the zen layout
  const zenRoutes = [
    '/referrals/new',
    '/referrals/add-patient',
    '/login',
    '/signup',
    '/verify',
    '/forgot-password',
    '/reset-password',
    '/set-password',
    '/onboarding',
    '/baa',
  ];

  // Check if current route is a catch-all route (404)
  const isCatchAllRoute = matches.some(match => match.id === 'routes/$');
  const isZenRoute = zenRoutes.includes(location.pathname);

  // Render the embed route without any layout
  if (isEmbedRoute) {
    return (
      <>
        {envScript}
        <Outlet />
      </>
    );
  }

  // Show landing page only for root route when not authenticated
  if (!isAuthenticated && location.pathname === '/') {
    return (
      <>
        {envScript}
        <HomePage />
      </>
    );
  }

  if (bareLayout) {
    return (
      <>
        <Outlet />
      </>
    );
  }

  // Branded demo landing only — no app nav/footer (logged-in or not)
  if (isDemoPublicCompanySiteRoute) {
    return (
      <>
        {envScript}
        <Outlet />
      </>
    );
  }

  // Show zen layout for focused pages and catch-all routes (404s)
  if (isZenRoute || isCatchAllRoute) {
    return wrapWithSupport(
      <>
        <ZenLayout>
          <Outlet />
        </ZenLayout>
      </>
    );
  }

  if (
    location.pathname.startsWith('/demo-builder') ||
    location.pathname.startsWith('/contact-us-builder')
  ) {
    return (
      <>
        {envScript}
        <PublicLayout hideFooter>
          <Outlet />
        </PublicLayout>
      </>
    );
  }

  // Show public routes without authentication
  if (!isAuthenticated && isPublicRoute) {
    return (
      <>
        {envScript}
        <PublicLayout>
          <Outlet />
        </PublicLayout>
      </>
    );
  }

  // Show authenticated layout for authenticated users or redirect to login
  return isAuthenticated ? (
    wrapWithSupport(
      <>
        {envScript}
        <AuthenticatedLayout>
          <Outlet />
        </AuthenticatedLayout>
      </>
    )
  ) : (
    <>
      {envScript}
      <HomePage />
    </>
  );
}
